/** Reference pricing (USD). PHP shown at approximate market rate for display only. */
export const USD_TO_PHP_RATE = 56;

/** Shown in profile modal helper text */
export const USD_TO_PHP_LABEL = `1 USD ≈ ${USD_TO_PHP_RATE} PHP (indicative)`;

export type BillingTierKey = 'blo' | 'plantation_owner' | 'lgu' | 'project_developer';

export type ProfileCurrency = 'USD' | 'PHP';

/** Display toggle in profile / checkout (pricing reference only until gateway is wired). */
export type BillingCadenceDisplay = 'monthly' | 'annual';

/** Applied to quoted monthly-equivalent USD when yearly billing is selected (20% reduction). */
export const ANNUAL_BILLING_DISCOUNT_FACTOR = 0.8;

export const CURRENCY_STORAGE_KEY = 'digisaka_profile_currency';

export interface BillingTier {
  key: BillingTierKey;
  label: string;
  baseHoursPerDay: number;
  monthlyPackHours: number;
  usd: { base: number; pack: number; extraPerHour: number };
}

export const BILLING_TIERS: Record<BillingTierKey, BillingTier> = {
  blo: {
    key: 'blo',
    label: 'BLO (big land owner)',
    baseHoursPerDay: 1,
    monthlyPackHours: 35,
    usd: { base: 5, pack: 8, extraPerHour: 0.18 },
  },
  plantation_owner: {
    key: 'plantation_owner',
    label: 'Plantation owner',
    baseHoursPerDay: 1,
    monthlyPackHours: 35,
    usd: { base: 10, pack: 13, extraPerHour: 0.3 },
  },
  lgu: {
    key: 'lgu',
    label: 'LGU',
    baseHoursPerDay: 2,
    monthlyPackHours: 70,
    usd: { base: 25, pack: 35, extraPerHour: 0.4 },
  },
  project_developer: {
    key: 'project_developer',
    label: 'Carbon Project Developer',
    baseHoursPerDay: 2,
    monthlyPackHours: 70,
    usd: { base: 25, pack: 35, extraPerHour: 0.4 },
  },
};

export function packMonthlyMs(tier: BillingTier): number {
  return tier.monthlyPackHours * 3600 * 1000;
}

/** Monthly plan: extra hours cannot exceed the pack size; user must pick Unlimited for a higher add-on cap. */
export function maxMonthlySelfServiceExtraHours(tier: BillingTier): number {
  return tier.monthlyPackHours;
}

/** Unlimited plan: add-on cap is twice the tier monthly pack (e.g. 35 → up to 70 extra hours). */
export function maxUnlimitedSelfServiceExtraHours(tier: BillingTier): number {
  return 2 * tier.monthlyPackHours;
}

export function tierKeyFromRole(role: string | undefined): BillingTierKey {
  const r = (role || 'blo').toLowerCase();
  if (r === 'plantation_owner' || r === 'banana_plantation') return 'plantation_owner';
  if (r === 'lgu' || r === 'municipal') return 'lgu';
  if (r === 'project_developer') return 'project_developer';
  return 'blo';
}

export function formatMoney(amountUsd: number, currency: ProfileCurrency): string {
  if (currency === 'USD') {
    return `$${amountUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  const php = Math.round(amountUsd * USD_TO_PHP_RATE);
  return `₱${php.toLocaleString('en-PH')}`;
}

/** Whole counts on plan cards, e.g. 5,376 hours. */
export function formatCount(value: number): string {
  return Number(value).toLocaleString('en-US');
}

/** Reference monthly USD with optional annual discount for display-only lines. */
export function displayMonthlyUsdForCadence(
  monthlyListUsd: number,
  cadence: BillingCadenceDisplay
): number {
  if (cadence === 'annual') return monthlyListUsd * ANNUAL_BILLING_DISCOUNT_FACTOR;
  return monthlyListUsd;
}

export function readStoredCurrency(): ProfileCurrency {
  if (typeof localStorage === 'undefined') return 'PHP';
  const v = localStorage.getItem(CURRENCY_STORAGE_KEY);
  return v === 'USD' ? 'USD' : 'PHP';
}

export function writeStoredCurrency(c: ProfileCurrency): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(CURRENCY_STORAGE_KEY, c);
}
